import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, Clock, CheckCircle, Eye, AlertCircle, Award, ShieldAlert, BarChart3, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => {
  const token = localStorage.getItem('staff_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : null;
};

const TYPE_HEX = {
  sickness: '#F59E0B', absence: '#F97316',
  warning_verbal: '#F59E0B', warning_written: '#F43F5E', warning_final: '#F43F5E',
  disciplinary_invite: '#F43F5E', investigation_invite: '#8A2BE2', absence_review_invite: '#00E5FF',
  outcome_dismissal: '#F43F5E', outcome_no_action: '#10B981', outcome_final_warning: '#F43F5E',
  outcome_action_plan: '#00E5FF', commendation: '#10B981', note: '#94A3B8',
};

export const HRAnalytics = ({ onOpenStaff }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const h = getAuthHeaders(); if (!h) return;
      try {
        const r = await axios.get(`${BACKEND_URL}/api/hr-notices/analytics`, { headers: h });
        setData(r.data);
      } catch {}
      setLoading(false);
    };
    load();
    const t = setInterval(load, 60000); // refresh every 60s
    return () => clearInterval(t);
  }, []);

  if (loading) return <div className="text-center py-14 text-white/40 text-sm">Loading analytics…</div>;
  if (!data) return <div className="text-center py-14 text-white/40 text-sm">No analytics available.</div>;

  const t = data.totals || {};
  const maxTrend = Math.max(1, ...(data.trend_30d || []).map(d => d.count));
  const trendUp = (t.trend_delta_pct || 0) >= 0;

  const KPI = ({ icon: Icon, label, value, sub, hex, testid }) => (
    <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4" data-testid={testid}>
      <div className="pointer-events-none absolute -top-8 -right-8 w-24 h-24 rounded-full blur-3xl opacity-40" style={{ background: hex }} />
      <div className="relative">
        <Icon className="w-4 h-4 mb-2" style={{ color: hex }} />
        <p className="text-3xl font-light text-white tabular-nums leading-none">{value}</p>
        <p className="text-[10px] tracking-[0.14em] uppercase text-white/45 mt-2">{label}</p>
        {sub && <p className="text-[10px] text-white/35 mt-1">{sub}</p>}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Hero KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI icon={BarChart3} label="Total notices" value={t.total} hex="#00E5FF" sub={`${t.last_30_days} in last 30d`} testid="analytics-total" />
        <KPI icon={CheckCircle} label="Response rate" value={`${t.response_rate}%`} hex="#10B981" sub={`${t.acknowledged} of ${t.total} acked`} testid="analytics-response-rate" />
        <KPI icon={Eye} label="Read rate" value={`${t.read_rate}%`} hex="#8A2BE2" sub={`${t.read} opened`} testid="analytics-read-rate" />
        <KPI icon={Clock} label="Avg time to ack" value={t.avg_ack_hours < 24 ? `${t.avg_ack_hours}h` : `${(t.avg_ack_hours / 24).toFixed(1)}d`} hex="#F59E0B" sub={`Median ${t.median_ack_hours}h`} testid="analytics-ack-time" />
      </div>

      {/* Trend + Priority side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* 30-day trend */}
        <div className="lg:col-span-2 relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">30 · day volume</p>
              <h3 className="text-white text-lg font-medium tracking-tight mt-0.5">Notices issued</h3>
            </div>
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${trendUp ? 'bg-emerald-500/[0.08] border border-emerald-500/25 text-emerald-300' : 'bg-rose-500/[0.08] border border-rose-500/25 text-rose-300'}`}>
              {trendUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {trendUp ? '+' : ''}{t.trend_delta_pct}% vs prev 30d
            </span>
          </div>
          <div className="flex items-end gap-1 h-32">
            {(data.trend_30d || []).map((d, i) => {
              const pct = (d.count / maxTrend) * 100;
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group relative">
                  <div className="w-full rounded-t bg-gradient-to-t from-cyan-500/70 to-violet-500/50 group-hover:from-cyan-400 group-hover:to-violet-400 transition-all"
                    style={{ height: `${Math.max(2, pct)}%` }} title={`${d.date}: ${d.count}`} />
                  {i % 5 === 0 && <span className="text-[8px] text-white/25 rotate-45 origin-top-left absolute -bottom-3">{d.date.slice(5)}</span>}
                </div>
              );
            })}
          </div>
          <div className="mt-6 flex items-center justify-between text-[10px] text-white/35">
            <span>{data.trend_30d?.[0]?.date || ''}</span>
            <span>{data.trend_30d?.[data.trend_30d.length - 1]?.date || ''}</span>
          </div>
        </div>

        {/* Priority breakdown */}
        <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
          <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">Priority mix</p>
          <h3 className="text-white text-lg font-medium tracking-tight mt-0.5 mb-3">By urgency</h3>
          <div className="space-y-3">
            {[
              { id: 'urgent', label: 'Urgent', hex: '#F43F5E' },
              { id: 'high', label: 'High', hex: '#F59E0B' },
              { id: 'normal', label: 'Normal', hex: '#94A3B8' },
            ].map(p => {
              const count = data.priority_breakdown?.[p.id] || 0;
              const pct = t.total ? Math.round((count / t.total) * 100) : 0;
              return (
                <div key={p.id} className="space-y-1">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-white/75 font-medium">{p.label}</span>
                    <span className="text-white/50 tabular-nums">{count} · {pct}%</span>
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: p.hex, boxShadow: `0 0 12px -2px ${p.hex}` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Type breakdown + Top recipients */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
          <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">Notice types</p>
          <h3 className="text-white text-lg font-medium tracking-tight mt-0.5 mb-3">Breakdown</h3>
          <div className="space-y-2 max-h-64 overflow-y-auto jewel-scroll pr-1">
            {(data.type_breakdown || []).slice(0, 12).map(row => {
              const hex = TYPE_HEX[row.type] || '#94A3B8';
              const pct = t.total ? Math.round((row.count / t.total) * 100) : 0;
              return (
                <div key={row.type} className="flex items-center gap-3 text-[11px]">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: hex }} />
                  <span className="text-white/80 flex-1 truncate">{row.label}</span>
                  <span className="text-white/40 tabular-nums w-8 text-right">{row.count}</span>
                  <div className="w-24 h-1 rounded-full bg-white/[0.05] overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: hex }} />
                  </div>
                  <span className="text-white/40 tabular-nums w-8 text-right">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-3.5 h-3.5 text-cyan-300" />
            <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">Top recipients</p>
          </div>
          <div className="space-y-1.5 max-h-64 overflow-y-auto jewel-scroll pr-1">
            {(data.top_recipients || []).length === 0 && <p className="text-white/30 text-xs italic text-center py-6">No data yet</p>}
            {(data.top_recipients || []).map((r, i) => (
              <button key={r.username} onClick={() => onOpenStaff && onOpenStaff(r.username)}
                data-testid={`analytics-top-${r.username}`}
                className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-white/[0.04] border border-transparent hover:border-white/[0.08] transition-all">
                <span className="w-6 h-6 rounded-full bg-white/[0.06] flex items-center justify-center text-[10px] text-white/60 font-bold flex-shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-white text-[12px] font-medium truncate">{r.name}</p>
                  <p className="text-white/40 text-[10px]">@{r.username}</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {r.warnings > 0 && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-bold bg-rose-500/10 border border-rose-500/30 text-rose-300"><ShieldAlert className="w-2.5 h-2.5" />{r.warnings}</span>}
                  {r.outcomes > 0 && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-bold bg-emerald-500/10 border border-emerald-500/30 text-emerald-300"><Award className="w-2.5 h-2.5" />{r.outcomes}</span>}
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-cyan-500/10 border border-cyan-500/30 text-cyan-300">{r.count}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRAnalytics;
